import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'

import { i18WithDefault as t } from '@shared/locales'
import type { ReceiptDto } from 'service-api/src/types/api'

type SurchargeFees =
  | {
      label?: string | undefined
      amount?: string | undefined
    }[]
  | undefined

// Renders the label/amount rows for a single policy card, with dividers between rows.
const SurchargeFeeRows = ({ fees }: { fees?: SurchargeFees }) => {
  return (
    <>
      {fees?.map((f, fIdx) => (
        <Stack key={fIdx}>
          <Box display="flex" flexDirection="row" alignItems="baseline" justifyContent="space-between" gap={1}>
            <Typography variant="body2" sx={{ minWidth: 0 }}>
              {f.label}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {f.amount}
            </Typography>
          </Box>

          {fIdx !== (fees.length ?? Infinity) - 1 && <Divider sx={{ my: 1 }} />}
        </Stack>
      ))}
    </>
  )
}

// Renders the surcharge policies captured in the receipt snapshot returned by the receipt API.
// When an order is booked we take a snapshot of the applicable policies, and it takes priority
// over the booking config when displaying surcharge policies after order confirmation.
export const ReceiptSurchargePolicy = ({ receipt }: { receipt: ReceiptDto }) => {
  const theme = useTheme()

  const departurePolicies = receipt.surchargePolicy?.departures ?? []
  const arrivalPolicies = receipt.surchargePolicy?.arrival

  return (
    <Stack mt={2} gap={2}>
      {arrivalPolicies && (
        <Stack sx={{ border: `1px solid ${theme.palette.divider}`, p: 2, borderRadius: 1 }}>
          <Typography variant="body1" style={{ fontWeight: 700 }}>
            {`${arrivalPolicies.header}`}
          </Typography>
          <Typography variant="caption" style={{ fontStyle: 'italic', color: theme.palette.text.secondaryShade60 }}>
            {`${t('surchargePolicy.open')}: ${arrivalPolicies.openHours}`}
          </Typography>
          {arrivalPolicies.handoffHours && (
            <Typography variant="body2" mb={1}>
              <strong>{t('surchargePolicy.checkInHours')}:</strong> {arrivalPolicies.handoffHours}
            </Typography>
          )}
          {(arrivalPolicies.fees?.length ?? 0) > 0 && (
            <Box sx={{ backgroundColor: theme.palette.background.grey, borderRadius: 1, p: 1 }}>
              <SurchargeFeeRows fees={arrivalPolicies.fees} />
            </Box>
          )}
        </Stack>
      )}

      {departurePolicies.map((d, i) => {
        return (
          <Stack key={i} sx={{ border: `1px solid ${theme.palette.divider}`, p: 2, borderRadius: 1 }}>
            <Typography variant="body1" style={{ fontWeight: 700 }}>
              {`${d.header}`}
            </Typography>
            <Typography variant="caption" style={{ fontStyle: 'italic', color: theme.palette.text.secondaryShade60 }}>
              {`${t('surchargePolicy.open')}: ${d.openHours}`}
            </Typography>
            {d.handoffHours && (
              <Typography variant="body2" mb={1}>
                <strong>{t('surchargePolicy.checkOutHours')}:</strong> {d.handoffHours}
              </Typography>
            )}
            {(d.fees?.length ?? 0) > 0 && (
              <Box sx={{ backgroundColor: theme.palette.background.grey, borderRadius: 1, p: 1 }}>
                <SurchargeFeeRows fees={d.fees} />
              </Box>
            )}
          </Stack>
        )
      })}
    </Stack>
  )
}
